import { FC, useMemo } from 'react';

import Chart from 'react-apexcharts';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/esm/styles/prism';
import remarkGfm from 'remark-gfm';
import { ToadIcon } from 'ui/components/icon';
import { cn } from 'utils/helpers';
import { ToadMessage } from 'utils/types';

interface Props extends ToadMessage {
  type?: 'sidebar' | 'page';
}

const ChatResponse: FC<Props> = ({ content, role, type }) => {
  const component = useMemo(() => {
    const isJSON = (text: string) => {
      try {
        JSON.parse(text);
        return true;
      } catch (e) {
        return false;
      }
    };

    const isTable = (text: string) =>
      text.includes('|') && text.includes('---');

    const isChartData = (text: string) => {
      try {
        const json = JSON.parse(text);
        return json.series && json.options;
      } catch (e) {
        return false;
      }
    };

    if (isChartData(content)) {
      const chartData = JSON.parse(content);
      return (
        <Chart
          options={chartData.options}
          series={chartData.series}
          type="bar"
          height={350}
        />
      );
    } else if (isJSON(content)) {
      return (
        <SyntaxHighlighter language="json" style={dracula}>
          {JSON.stringify(JSON.parse(content), null, 2)}
        </SyntaxHighlighter>
      );
    } else if (isTable(content)) {
      return (
        <ReactMarkdown className="text-sm prose" remarkPlugins={[remarkGfm]}>
          {content}
        </ReactMarkdown>
      );
    } else {
      return <ReactMarkdown className="text-sm prose">{content}</ReactMarkdown>;
    }
  }, [content]);

  return (
    <div
      className={cn(
        'flex gap-2 text-sm',
        role?.toLowerCase() === 'user' ? 'flex-row-reverse' : '',
        'max-w-none',
      )}
    >
      {role === 'assistant' && (
        <div
          className={cn(
            'flex-shrink-0 mt-[14px]',
            'w-10 h-10 flex items-center justify-center',
          )}
        >
          <ToadIcon className="w-9 h-9 text-primary-base flex-shrink-0" />
        </div>
      )}
      <div
        className={cn(
          'p-[14px] rounded-lg max-w-full',
          role.toLowerCase() === 'assistant'
            ? 'bg-none'
            : type === 'sidebar'
              ? 'bg-[#F1F4F8] px-4'
              : 'bg-fill-tertiary px-4',
        )}
      >
        {role.toLowerCase() === 'assistant' && (
          <div className="font-semibold text-xs py-2.5">Toad AI Result</div>
        )}
        {component}
      </div>
    </div>
  );
};

export default ChatResponse;
